// @core/stores/billing/BillingUsageStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  CreateUsageRecordBatchRequest,
  CreateUsageRecordBatchResponse,
  CreateUsageRecordRequest,
  GetUsageSummaryResponse,
  UsageRecord,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingUsageStore {
  // List state
  items: UsageRecord[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: UsageRecord | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, UsageRecord>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): UsageRecord | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: UsageRecord | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create usage record
   */
  async CreateUsageRecord(data: CreateUsageRecordRequest): Promise<UsageRecord> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateUsageRecord(data);
      const item = response.usage_record;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create multiple usage records
   */
  async CreateUsageRecordBatch(data: CreateUsageRecordBatchRequest): Promise<CreateUsageRecordBatchResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.CreateUsageRecordBatch(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get usage summary
   */
  async GetUsageSummary(params?: { billing_account_id?: string | number | boolean; period_end?: string | number | boolean; period_start?: string | number | boolean; usage_type?: string | number | boolean; }): Promise<GetUsageSummaryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await billingApi.GetUsageSummary(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List usage records
   */
  async ListUsageRecords(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListUsageRecords(params);

      runInAction(() => {
        this.items = response.usage_records?.records ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const billingUsageStore = new BillingUsageStore();
